import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';

const { fontFamilies, fontSizes, fontWeights } = THEME.typography;

const LEADER_COLUMN_WIDTH = 136;
const DEMOS_COLUMN_WIDTH = 96;
const DEMOS_PROGRESS_WIDTH = 200;
const BADGE_SIZE = 36;

const tableFrame = {
	border: `1px solid ${THEME.colors.border.light}`,
	borderRadius: THEME.common.borderRadius,
	boxSizing: 'border-box',
	overflow: 'hidden',
	width: '100%',
} as const;

export const styleSheet = StyleSheet.create({
	board: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[40],
		width: '100%',
	},
	dayBoard: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		width: '100%',
	},
	heading: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.xl,
		fontWeight: fontWeights.bold,
		margin: `${SPACE[8]}px 0`,
		textAlign: 'center',
		[THEME.queries.sm]: {
			fontSize: fontSizes.lg,
		},
	},
	totalDials: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.bold,
	},

	// Demo totals
	totals: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[16],
		justifyContent: 'center',
	},
	totalsTitle: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.bold,
	},

	// Podium
	podium: {
		alignItems: 'flex-end',
		display: 'flex',
		gap: SPACE[56],
		justifyContent: 'center',
		listStyle: 'none',
		margin: 0,
		padding: `0 0 ${SPACE[20]}px`,
		[THEME.queries.sm]: {
			gap: SPACE[24],
		},
	},
	podiumPlace: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		position: 'relative',
	},
	podiumPlaceEmpty: {
		visibility: 'hidden',
	},
	podiumRing: {
		borderRadius: '50%',
		borderStyle: 'solid',
		borderWidth: 2,
		padding: SPACE[4],
	},
	podiumRingFirst: {
		borderColor: THEME.colors.chart.illusionYellow,
	},
	podiumRingSecond: {
		borderColor: THEME.colors.chart.levBlue,
	},
	podiumRingThird: {
		borderColor: THEME.colors.chart.tricksterGreen,
	},
	podiumAvatar: {
		height: 96,
		width: 96,
		[THEME.queries.sm]: {
			height: 64,
			width: 64,
		},
	},
	podiumAvatarFirst: {
		height: 144,
		width: 144,
		[THEME.queries.sm]: {
			height: 96,
			width: 96,
		},
	},
	podiumBadge: {
		alignItems: 'center',
		borderRadius: '50%',
		bottom: -(BADGE_SIZE / 2),
		color: THEME.colors.text.main,
		display: 'flex',
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.bold,
		height: BADGE_SIZE,
		justifyContent: 'center',
		left: '50%',
		position: 'absolute',
		transform: 'translateX(-50%)',
		width: BADGE_SIZE,
	},
	podiumBadgeFirst: {
		backgroundColor: THEME.colors.chart.illusionYellow,
	},
	podiumBadgeSecond: {
		backgroundColor: THEME.colors.chart.levBlue,
		color: THEME.colors.background.light,
	},
	podiumBadgeThird: {
		backgroundColor: THEME.colors.chart.tricksterGreen,
	},
	crownUpsideDown: {
		transform: 'rotate(180deg)',
	},

	// Leaders table
	table: tableFrame,
	leaderRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'grid',
		fontSize: fontSizes.md,
		fontVariantNumeric: 'tabular-nums',
		gridTemplateColumns: `minmax(0, 1fr) repeat(5, ${LEADER_COLUMN_WIDTH}px)`,
		height: 44,
		padding: `0 ${SPACE[12]}px 0 ${SPACE[20]}px`,
	},
	leaderRowSingleColumn: {
		gridTemplateColumns: `minmax(0, 1fr) ${LEADER_COLUMN_WIDTH}px`,
	},
	leaderHeaderRow: {
		backgroundColor: THEME.colors.background.main,
		borderTop: 'none',
		color: THEME.colors.text.light,
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.semibold,
	},
	leaderName: {
		...THEME.common.truncate,
		fontWeight: fontWeights.semibold,
	},
	leaderCell: {
		paddingRight: SPACE[12],
		textAlign: 'right',
	},
	leaderCellSorted: {
		fontWeight: fontWeights.bold,
	},
	leaderCellPoints: {
		color: THEME.colors.primary.main,
	},
	leaderCellConnections: {
		color: THEME.colors.success.subtle.dark,
	},
	sortHeader: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
	sortButton: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.semibold,
		gap: SPACE[4],
	},
	sortButtonActive: {
		color: THEME.colors.primary.main,
		fontWeight: fontWeights.bold,
	},
	sortIcon: {
		height: 16,
		width: 16,
	},

	// Demos tables
	demosTables: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[40],
		marginTop: SPACE[8],
		maxWidth: 748,
		width: '100%',
	},
	demosTable: tableFrame,
	demosRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'grid',
		fontSize: fontSizes.sm,
		fontVariantNumeric: 'tabular-nums',
		gridTemplateColumns: `minmax(0, 1fr) ${DEMOS_COLUMN_WIDTH}px ${DEMOS_COLUMN_WIDTH}px ${DEMOS_PROGRESS_WIDTH}px`,
		height: 40,
		padding: `0 ${SPACE[20]}px`,
		[THEME.queries.sm]: {
			gridTemplateColumns: `minmax(0, 1fr) ${DEMOS_COLUMN_WIDTH}px ${DEMOS_COLUMN_WIDTH}px`,
			padding: `0 ${SPACE[16]}px`,
		},
	},
	demosHeaderRow: {
		backgroundColor: THEME.colors.background.main,
		borderTop: 'none',
		fontWeight: fontWeights.bold,
		height: 44,
	},
	demosTotalRow: {
		borderTop: `1px solid ${THEME.colors.border.main}`,
		fontWeight: fontWeights.bold,
		height: 44,
	},
	demosLabel: {
		...THEME.common.truncate,
	},
	demosHeaderCell: {
		color: THEME.colors.text.light,
		fontWeight: fontWeights.semibold,
		textAlign: 'right',
	},
	demosGoal: {
		color: THEME.colors.text.light,
		textAlign: 'right',
	},
	demosTotalCell: {
		textAlign: 'right',
	},
	demosActual: {
		fontWeight: fontWeights.semibold,
		textAlign: 'right',
	},
	demosProgress: {
		paddingLeft: SPACE[32],
	},
	demosProgressHeader: {
		color: THEME.colors.text.light,
		fontWeight: fontWeights.semibold,
		paddingLeft: SPACE[32],
	},

	// Prizes list
	prizes: {
		...tableFrame,
		maxWidth: 640,
	},
	prizeRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'grid',
		fontSize: fontSizes.md,
		gap: SPACE[8],
		gridTemplateColumns: '32px minmax(0, 1fr) 128px 180px',
		minHeight: 52,
		padding: `${SPACE[8]}px ${SPACE[20]}px`,
		':first-child': {
			borderTop: 'none',
		},
		[THEME.queries.sm]: {
			gridTemplateColumns: '32px minmax(0, 1fr)',
			padding: `${SPACE[8]}px ${SPACE[16]}px`,
		},
	},
	prizeCoin: {
		display: 'block',
	},
	prizeName: {
		...THEME.common.truncate,
		fontWeight: fontWeights.semibold,
	},
	prizeDate: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.sm,
		fontVariantNumeric: 'tabular-nums',
	},
	prizeReward: {
		color: THEME.colors.text.main,
		fontWeight: fontWeights.bold,
	},
	prizeMobileDetails: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
});
